import './styles/base.css'
import './styles/app.css'
import './styles/orb.css'
import './styles/home.css'
import './styles/chrome.css'
import './styles/orbit.css'
import './styles/screens.css'
import './styles/ceremony.css'

import { apiGet, apiPost, loadSession, ApiError } from './js/api.js'
import { syncThemeColor } from './js/theme.js'
import { defineRoutes, startRouter, navigate, match, currentPath } from './js/router.js'
import { registerServiceWorker, watchConnection } from './js/sw-register.js'
import { renderHeader, SECTIONS } from './components/chrome.js'
import { watchSwipes } from './js/swipe.js'
import { showOrbitHint, cancelOrbitHint } from './components/orbitHint.js'
import { openOrbitMenu, closeOrbitMenu, isOrbitOpen } from './components/orbitMenu.js'
import { icon } from './js/icons.js'
import { enablePush, disablePush, pushStatus } from './js/push.js'
import { isIOS, isStandalone } from './js/platform.js'

import { renderLogin } from './pages/login.js'
import { renderTokenEntry } from './pages/token-entry.js'
import { renderOnboarding } from './pages/onboarding.js'
import { renderHome, mountHome, markPathSeen, alertsFor } from './pages/home.js'
import { renderPath } from './pages/path.js'
import { renderBrief } from './pages/brief.js'
import { renderQuestionnaire, renderResult } from './pages/questionnaire.js'
import { renderPrivacy } from './pages/privacy.js'
import { startCountdown } from './components/countdown.js'
import {
  renderAdminRetreats,
  renderAdminBriefs,
  renderAdminDelivery,
  renderAdminGallery,
  renderAdminSettings,
  renderAdminSystem,
} from './pages/admin.js'
import { renderAdminQuestionnaires } from './pages/admin-questionnaires.js'
import { renderGallery, mountGallery } from './pages/gallery.js'
import { renderGroup, mountGroup } from './pages/group.js'
import { renderYou, mountYou } from './pages/you.js'
import { renderPractical, mountPractical } from './pages/practical.js'

const root = document.querySelector('#app')

/** True once boot has painted; see the connection watcher below. */
let booted = false

syncThemeColor()
registerServiceWorker()

/** Everything the shell needs to draw itself. */
const state = {
  session: null,
  retreat: null,
  timeline: [],
  acts: {},
  orb: null,
  push: { subscriptions: 0, channel: 'email' },
  gallery: null,
  group: null,
  inner: null,
  offline: !watchConnection((online) => {
    state.offline = !online

    // Not before the first paint. The browser fires `offline` while boot is
    // still fetching, and repainting then would put a screen on the glass with
    // data that has not arrived yet — Home without its day, which is how the
    // offline reload used to come back empty every so often.
    if (booted) scheduleRender()
  }),
}

/**
 * Screens that need their own data fetch it the first time they are opened,
 * then re-render. `key` is where the result lands on the state.
 */
const SCREENS = [
  // Home is the root. The timeline it used to hold is now Path, a screen of
  // its own — "Journey" is not a screen name anywhere (REDESIGN.md §5).
  { pattern: /^\/$/, render: renderHome, mount: mountHome, title: 'Home', section: 'home', chrome: 'bare' },
  { pattern: /^\/home$/, render: renderHome, mount: mountHome, title: 'Home', section: 'home', chrome: 'bare' },
  {
    pattern: /^\/path$/,
    render: renderPath,
    mount: (_root, context) => markPathSeen(context.timeline),
    title: 'Path',
    section: 'path',
  },
  {
    pattern: /^\/galerija$/,
    render: renderGallery,
    key: 'gallery',
    endpoint: '/gallery',
    mount: mountGallery,
    title: 'Gallery',
    section: 'gallery',
  },
  {
    pattern: /^\/grupa$/,
    render: renderGroup,
    key: 'group',
    endpoint: '/group',
    mount: mountGroup,
    title: 'Group',
    section: 'group',
  },
  {
    pattern: /^\/ti$/,
    render: renderYou,
    key: 'inner',
    endpoint: null,
    mount: mountYou,
    title: 'You',
    section: 'you',
  },
  // Practical belongs to no section, so no circle reads as current on it.
  {
    pattern: /^\/prakticno$/,
    render: renderPractical,
    mount: mountPractical,
    title: 'Practical',
    section: null,
  },
  { pattern: /^\/brief\/(\d+)$/, name: 'brief' },
  { pattern: /^\/upitnik\/(\d+)$/, name: 'questionnaire' },
  { pattern: /^\/upitnik\/(\d+)\/rezultat$/, name: 'result' },
  { pattern: /^\/privatnost$/, name: 'privacy' },
  // Admin paths are English, unlike the participant ones: these are new or
  // renamed in this pass, and nobody outside the app links to them.
  { pattern: /^\/admin$/, name: 'admin' },
  { pattern: /^\/admin\/delivery$/, name: 'admin-delivery' },
  { pattern: /^\/admin\/system$/, name: 'admin-system' },
  { pattern: /^\/admin\/retreat\/(\d+)$/, name: 'admin-retreat' },
  { pattern: /^\/admin\/retreat\/(\d+)\/gallery$/, name: 'admin-gallery' },
  { pattern: /^\/admin\/retreat\/(\d+)\/settings$/, name: 'admin-settings' },
  { pattern: /^\/admin\/retreat\/(\d+)\/questions$/, name: 'admin-questions' },
  { pattern: /^\/(join|magic)\/([A-Za-z0-9_-]+)$/, name: 'token' },
]

defineRoutes(SCREENS, render)


// ---- screen transitions ----------------------------------------------------

/**
 * Screens slide left and right rather than fading (Željko's call, overriding
 * REDESIGN.md §6, which asked for a fade through white and no slide).
 *
 * A slide only means something if its direction does, so it comes from the one
 * order the app already has — Home, Path, Gallery, Group, You, Practical — with
 * a sub-page half a step past its own section. Further down the list arrives
 * from the right; coming back arrives from the left.
 *
 * The screen being left is not a copy. The real node is moved, so a canvas
 * mid-frame keeps its pixels and the Orb does not blink out on the way off.
 *
 * It moves out of #app while it does, into a layer of its own. For a third of a
 * second two screens exist, and every `root.querySelector` in this file would
 * otherwise have to guess which one it meant — the live header or the one on
 * its way off the glass.
 */
const ORDER = ['home', 'path', 'gallery', 'group', 'you']

/** Must match the slide in chrome.css. */
const SLIDE_MS = 300

/** Where a sub-page's swipe back goes — the same place its back control does. */
const SUBPAGE_PARENT = {
  brief: '/path',
  questionnaire: '/ti',
  result: '/ti',
  privacy: '/ti',
}

/**
 * Where a swipe lands, or null when there is nowhere to go that way.
 *
 * Same order as the slide, because they are the same movement: a swipe is only
 * the lazy way to do what the orbit menu does deliberately.
 */
function swipeTarget(direction) {
  if (!state.session?.authenticated) return null
  if (state.session.onboarding && !state.session.onboarding.complete) return null

  const route = match()

  if (route === null || route.name?.startsWith('admin') === true) return null

  const parent = route.name === undefined ? undefined : SUBPAGE_PARENT[route.name]

  if (parent !== undefined) {
    return direction === 'back' ? parent : null
  }

  // Practical belongs to no section; the only way out of it is up.
  if (route.section === null) {
    return direction === 'back' ? '/' : null
  }

  if (route.section === undefined) return null

  const index = SECTIONS.findIndex((section) => section.key === route.section)

  if (index === -1) return null

  return SECTIONS[index + (direction === 'forward' ? 1 : -1)]?.path ?? null
}

function rankOf(section, detail = false) {
  const index = section === null ? -1 : ORDER.indexOf(section)

  return (index === -1 ? ORDER.length : index) + (detail ? 0.5 : 0)
}

let lastRank = null
let lastScreenPath = null
let leaving = null

/**
 * What the screen on its way out needs stopped.
 *
 * Home's Orb runs a requestAnimationFrame loop and mountHome hands back the way
 * to end it; nobody was calling it, so every visit left another loop drawing
 * into a canvas no longer on the page. It matters more now that the old screen
 * lingers for the length of a slide.
 */
let unmount = null

/** When the slide in flight ends, as a clock reading; 0 when nothing is moving. */
let slideEndsAt = 0
let queuedRender = false

/**
 * A repaint asked for by data arriving rather than by the person.
 *
 * render() replaces the whole shell, which tears a slide in half: the screen on
 * its way out disappears and the one arriving starts over. Nothing that lands
 * from the network is urgent enough to be worth that, so it waits for the move
 * to finish — about a third of a second.
 */
function scheduleRender() {
  const wait = slideEndsAt - performance.now()

  if (wait <= 0) {
    render()

    return
  }

  if (queuedRender) return

  queuedRender = true
  window.setTimeout(() => {
    queuedRender = false
    render()
  }, wait + 20)
}

/** A screen painted outside the shell (admin, onboarding) ends any sequence. */
function resetSwap() {
  lastRank = null
  lastScreenPath = null
  slideEndsAt = 0
}

/**
 * Takes the current screen off the glass, if this is a move between two screens
 * rather than a repaint of the one already there. Returns the direction, or null
 * when nothing should animate.
 */
function beginSwap(rank) {
  cancelOrbitHint()

  const path = currentPath()
  const previous = lastRank
  const repaint = path === lastScreenPath

  lastRank = rank
  lastScreenPath = path

  if (repaint || previous === null || rank === previous) return null
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return null

  const current = root.firstElementChild

  if (current === null) return null

  current.remove()
  leaving = { node: current, direction: rank > previous ? 'forward' : 'back' }

  return leaving.direction
}

/** Puts the new screen on top and lets the old one drift out beneath it. */
function endSwap(direction) {
  if (direction === null || leaving === null) {
    leaving = null

    return
  }

  const { node } = leaving
  leaving = null

  // Nothing in here is live any more: not for the keyboard, not for a screen
  // reader, and not for a getElementById that is about to look for a button.
  node.setAttribute('aria-hidden', 'true')
  node.inert = true
  node.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'))

  const layer = document.createElement('div')
  layer.className = 'screen-exit'
  layer.append(node)
  node.classList.add('screen-leave', `screen-leave--${direction}`)
  document.body.append(layer)

  slideEndsAt = performance.now() + SLIDE_MS
  root.classList.add('is-sliding')
  root.firstElementChild?.classList.add('screen-enter', `screen-enter--${direction}`)

  const done = () => {
    slideEndsAt = 0
    layer.remove()
    root.classList.remove('is-sliding')
    root.firstElementChild?.classList.remove('screen-enter', `screen-enter--${direction}`)
  }

  node.addEventListener('animationend', done, { once: true })
  // The listener is enough in practice; this is the net under a dropped frame
  // or a tab that was backgrounded mid-slide, where no animationend ever fires.
  window.setTimeout(done, 800)
}

// ---- rendering ----------------------------------------------------------

function render() {
  const route = match()

  if (route?.name === 'token') {
    renderTokenEntry(root, {
      kind: route.params[0],
      token: route.params[1],
      onSignedIn: (result) => {
        state.session = { authenticated: true, ...result }
        navigate('/', { replace: true })
        boot()
      },
      onFailed: (message) => renderSignedOut(message),
    })

    return
  }

  // Readable without an account: it is linked from the consent step.
  if (route?.name === 'privacy') {
    renderPrivacy(
      state.session?.authenticated
        ? detailRoot('Privacy', { to: '/ti', label: 'You' }, 'you')
        : root,
      { onBack: () => navigate(state.session?.authenticated ? '/ti' : '/') },
    )

    return
  }

  if (state.session === null || !state.session.authenticated) {
    renderSignedOut()

    return
  }

  const isAdmin = ['admin', 'cohost'].includes(state.session.user?.role)

  const onAdmin = route?.name?.startsWith('admin') === true
  root.classList.toggle('app-shell--wide', onAdmin)

  if (onAdmin) {
    root.classList.remove('app-shell--bare')
  }

  if (route?.name?.startsWith('admin')) {
    resetSwap()

    if (!isAdmin) {
      navigate('/', { replace: true })

      return
    }

    // Every admin screen sits in the same frame and navigates the same way.
    const chrome = { onNavigate: (path) => navigate(path), onExit: () => navigate('/') }
    const id = Number(route.params[0])

    if (route.name === 'admin') {
      renderAdminRetreats(root, chrome)
    } else if (route.name === 'admin-delivery') {
      renderAdminDelivery(root, chrome)
    } else if (route.name === 'admin-system') {
      renderAdminSystem(root, chrome)
    } else if (route.name === 'admin-gallery') {
      renderAdminGallery(root, { retreatId: id, ...chrome })
    } else if (route.name === 'admin-settings') {
      renderAdminSettings(root, { retreatId: id, ...chrome })
    } else if (route.name === 'admin-questions') {
      renderAdminQuestionnaires(root, { retreatId: id, ...chrome })
    } else {
      renderAdminBriefs(root, { retreatId: id, ...chrome })
    }

    return
  }

  if (route?.name === 'questionnaire' && state.session.onboarding?.complete) {
    renderQuestionnaire(detailRoot('Questionnaire', { to: '/ti', label: 'You' }, 'you'), {
      id: route.params[0],
      onBack: () => {
        state.inner = null
        navigate('/ti')
      },
    })

    return
  }

  if (route?.name === 'result' && state.session.onboarding?.complete) {
    renderResult(detailRoot('Result', { to: '/ti', label: 'You' }, 'you'), {
      id: route.params[0],
      onBack: () => navigate('/ti'),
    })

    return
  }

  if (route?.name === 'brief' && state.session.onboarding?.complete) {
    renderBrief(detailRoot('Brief', { to: '/path', label: 'Path' }, 'path'), {
      id: route.params[0],
      retreat: state.retreat,
      onBack: () => navigate('/path'),
    })

    return
  }

  const onboarding = state.session.onboarding

  if (onboarding && !onboarding.complete) {
    resetSwap()
    renderOnboarding(root, {
      user: state.session.user,
      onboarding,
      onUpdate: (user) => {
        state.session.user = { ...state.session.user, ...user }
      },
      onDone: boot,
    })

    return
  }

  renderShell(route)
}

/**
 * A sub-page is an inner screen too: same header, same orb button, plus the one
 * thing a section screen does not get — a text back control pointing at its
 * parent rather than at Home (NAVIGATION.md §2).
 *
 * @param {string} title
 * @param {{to: string, label: string}} back
 * @param {string|null} section which circle reads as current in the orbit menu
 */
function detailRoot(title, back = { to: '/', label: 'Home' }, section = null) {
  root.classList.toggle('app-shell--bare', true)
  root.classList.remove('app-shell--wide')

  const direction = beginSwap(rankOf(section, true))

  unmount?.()
  unmount = null

  root.innerHTML = `
    <div class="inner">
      ${renderHeader({ title, back, alert: hasAlerts() })}
      ${state.offline ? offlineBanner() : ''}
      <main class="inner__body" id="detail"></main>
    </div>
  `

  wireOrbitButton(section)
  endSwap(direction)

  return root.querySelector('#detail')
}

/**
 * The header orb opens the menu. Done here rather than inside the component so
 * that the one thing it needs from the app — where you are, and what is unseen —
 * comes from the same state Home reads, not a second copy of it (§5).
 */
function wireOrbitButton(section) {
  const button = root.querySelector('[data-orbit-open]')

  if (button === null) return

  teachOrbit(button)

  button.addEventListener('click', () => {
    if (isOrbitOpen()) {
      closeOrbitMenu()

      return
    }

    button.setAttribute('aria-expanded', 'true')
    openOrbitMenu({
      from: button,
      current: section,
      alerts: alertsFor(state.timeline),
      onClosed: () => button.setAttribute('aria-expanded', 'false'),
    })
  })
}

function hasAlerts() {
  return alertsFor(state.timeline).length > 0
}

/**
 * Teaches the orb once, to everyone, on whatever screen they first meet it.
 *
 * The flag lives on the user and not in this browser, so learning it on a phone
 * settles it for the laptop too. The local copy is set at the same moment as the
 * request so the hint cannot reappear on the next repaint while the POST is
 * still in the air; if the request fails the person simply gets taught again
 * next time, which is the harmless direction to fail in.
 */
function teachOrbit(button) {
  if (state.session?.user?.orbit_hint_seen !== false) return

  showOrbitHint(button, {
    onDismissed: () => {
      state.session.user.orbit_hint_seen = true
      apiPost('/me/orbit-hint').catch(() => {})
    },
  })
}

function renderSignedOut(notice = null) {
  resetSwap()
  renderLogin(root, {
    onSignedIn: () => boot(),
  })

  if (notice !== null) {
    const paragraph = document.createElement('p')
    paragraph.className = 'notice notice--error'
    paragraph.textContent = notice
    root.querySelector('#login-form')?.append(paragraph)
  }
}

function renderShell(route) {
  const screen = route?.render ?? renderHome
  // Home runs edge to edge: the same header, without the rule or the surface
  // behind it, and the wordmark where a screen name would be.
  const bare = route?.chrome === 'bare'

  root.classList.toggle('app-shell--bare', true)

  const direction = beginSwap(rankOf(route?.section ?? null))

  root.innerHTML = `
    <div class="inner">
      ${renderHeader({ title: route?.title ?? '', alert: hasAlerts(), bare })}
      ${state.offline ? offlineBanner() : ''}
      ${isAdminUser() ? adminBar() : ''}
      <main class="${bare ? '' : 'inner__body'}" id="screen"></main>
    </div>
  `

  root.querySelector('#to-admin')?.addEventListener('click', () => navigate('/admin'))

  const context = {
    user: state.session.user,
    retreat: state.retreat,
    timeline: state.timeline,
    acts: state.acts,
    orb: state.orb,
    offline: state.offline,
    push: state.push,
    gallery: state.gallery,
    group: state.group,
    inner: state.inner,
    // Screens that change their own data ask for a refetch rather than
    // patching state themselves.
    reload: () => {
      state.inner = null
      scheduleRender()
    },
  }

  const screenRoot = root.querySelector('#screen')
  screenRoot.innerHTML = screen(context)

  unmount?.()
  unmount = route?.mount?.(screenRoot, context) ?? null

  // After the screen is painted, not before: on Home the button lives in the
  // screen itself rather than in a header.
  wireOrbitButton(route?.section ?? null)
  endSwap(direction)

  root.querySelector('#push-on')?.addEventListener('click', () => togglePush(true))
  root.querySelector('#push-off')?.addEventListener('click', () => togglePush(false))

  loadScreenData(route)

  root.querySelector('#logout')?.addEventListener('click', async () => {
    await apiPost('/auth/logout')
    state.session = null
    state.retreat = null
    state.gallery = null
    state.group = null
    navigate('/', { replace: true })
    render()
  })
}

/**
 * Fetches a screen's data once, then repaints. Failures leave the screen in
 * its loading state rather than throwing the whole shell away.
 */
async function loadScreenData(route) {
  if (route?.key === undefined || state[route.key] !== null) {
    return
  }

  try {
    state[route.key] = route.key === 'inner' ? await loadInner() : await apiGet(route.endpoint)
  } catch {
    state[route.key] = route.key === 'group' ? { unlocked: false, members: [] } : { days: [] }
  }

  scheduleRender()
}

/** The "Ti" screen pulls from three places; one failing must not blank it. */
async function loadInner() {
  const [questionnaires, journal, capsules, me] = await Promise.all([
    apiGet('/questionnaires').catch(() => ({ questionnaires: [], trend: [] })),
    apiGet('/journal').catch(() => ({ today: null, entries: [], date: null })),
    apiGet('/capsules').catch(() => ({ capsules: [], max_video_bytes: 0 })),
    apiGet('/me').catch(() => ({ consents: [] })),
  ])

  return {
    questionnaires: questionnaires.questionnaires ?? [],
    trend: questionnaires.trend ?? [],
    journal,
    capsules: capsules.capsules ?? [],
    maxVideoBytes: capsules.max_video_bytes ?? 0,
    consents: me.consents ?? [],
  }
}

/**
 * Why turning push on did not work, in words the person can act on.
 *
 * This is the one setup step that can fail for reasons the app can see but the
 * person cannot, and it fails on the screen where they are least able to guess:
 * iOS refuses to subscribe at all from a browser tab, and says so by throwing.
 * Staying silent here left the button looking broken.
 */
function pushProblem(outcome) {
  if (outcome === 'unsupported') {
    return isIOS() && !isStandalone()
      ? 'On iPhone, notifications only work from the installed app. Share → Add to Home Screen, then open it from there.'
      : 'This browser does not support push notifications. Everything still reaches you by email.'
  }

  if (outcome === 'denied') {
    return 'Notifications were refused. Turn them on in your browser settings for this site, then try again.'
  }

  if (outcome === 'unavailable') {
    return 'Push is not set up on the server yet. Tell us — meanwhile everything arrives by email.'
  }

  return null
}

async function togglePush(on) {
  let outcome = null
  let problem = null

  try {
    outcome = on ? await enablePush() : await disablePush()
    problem = on ? pushProblem(outcome) : null
  } catch (error) {
    // Safari throws rather than returning when it will not subscribe, and an
    // unhandled throw here would leave the screen exactly as it was — which
    // reads as "the button does nothing".
    problem =
      isIOS() && !isStandalone()
        ? 'On iPhone, notifications only work from the installed app. Share → Add to Home Screen, then open it from there.'
        : `That did not work: ${error?.message ?? 'unknown error'}. Everything still reaches you by email.`
  }

  // Paint the new state straight away. Waiting on a status round-trip leaves
  // the button looking dead, and we already know what we just did.
  state.push = {
    subscriptions: on && outcome === 'subscribed' ? 1 : 0,
    problem,
  }
  render()

  // Then reconcile with the server, quietly — keeping the explanation, which
  // the server knows nothing about.
  pushStatus().then((status) => {
    state.push = { ...status, problem }
    scheduleRender()
  })
}

function isAdminUser() {
  return ['admin', 'cohost'].includes(state.session?.user?.role)
}

/** Admins see the participant app as it is, plus one way into the admin area. */
function adminBar() {
  return `
    <p class="admin-bar">
      <span>Viewing as admin</span>
      <button class="link-button" id="to-admin">Admin</button>
    </p>
  `
}

function offlineBanner() {
  return `
    <p class="offline-banner">
      ${icon('cloudOff', { size: 16 })}
      <span>No connection. You are seeing the last thing we saved.</span>
    </p>
  `
}

// ---- boot ---------------------------------------------------------------

async function boot() {
  try {
    const session = await loadSession()

    if (!session.authenticated) {
      state.session = session
      booted = true
      render()

      return
    }

    // /me carries the onboarding state the shell gates on.
    state.session = { ...session, ...(await apiGet('/me')) }

    if (state.session.onboarding?.complete) {
      try {
        const result = await apiGet('/journey')
        state.retreat = result.retreat
        state.timeline = result.timeline ?? []
        state.acts = result.acts ?? {}
        state.orb = result.orb ?? null
        state.push = await pushStatus()
      } catch (error) {
        state.retreat = null
        state.timeline = []
        state.acts = {}
        state.orb = null

        if (error instanceof ApiError && error.status !== 403) {
          throw error
        }
      }
    }

    booted = true
    render()
  } catch {
    booted = true
    root.innerHTML = `
      <h1 class="hero-title">Differ LX</h1>
      <section class="card">
        <p class="lede">The server is not answering. Try again in a minute.</p>
      </section>
    `
  }
}

startRouter()

watchSwipes((direction) => {
  // Not into a move that is still happening: the slide would start over and the
  // screen between the two would never be seen.
  if (performance.now() < slideEndsAt) return

  const target = swipeTarget(direction)

  if (target !== null) navigate(target)
})

boot()
